import { Fragment, useCallback, useEffect, useState } from "react";

import {
  APIError,
  agentUpdateView,
  deviceLabel,
  getAgentRelease,
  getDevices,
  isAgentReleaseNewer,
  requestMeasurementPointDeviceUpdate,
  supportsRemoteUpdates,
  updateRequiresMaintenance,
  type AgentRelease,
  type Device,
  type Identity,
} from "./api";
import { DeviceStorageCells } from "./Storage";
import { ExtensionHealth } from "./ExtensionHealth";
import { displayLocale } from "./localization";

export function TenantDeviceManagement({ identity, onSignedOut }: { identity: Identity; onSignedOut: () => void }) {
  const [points, setPoints] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [release, setRelease] = useState<AgentRelease | null>(null);
  const [updatingPointID, setUpdatingPointID] = useState("");
  const [extensionPointID, setExtensionPointID] = useState("");

  const handleError = useCallback((reason: unknown, fallback: string) => {
    if (reason instanceof APIError && reason.status === 401) {
      onSignedOut();
      return;
    }
    setError(fallback);
  }, [onSignedOut]);

  const reload = useCallback(async () => {
    const result = await getDevices();
    setPoints(result);
  }, []);

  useEffect(() => {
    let active = true;
    getDevices()
      .then((result) => { if (active) setPoints(result); })
      .catch((reason: unknown) => { if (active) handleError(reason, "Die Messstellen konnten nicht geladen werden."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [handleError]);

  useEffect(() => {
    getAgentRelease()
      .then(setRelease)
      .catch((reason: unknown) => handleError(reason, "Die Agent-Version konnte nicht geladen werden."));
  }, [handleError]);

  async function updateAgent(point: Device) {
    if (!release || !window.confirm(`${deviceLabel(point)} an der Messstelle „${point.location}“ jetzt auf Agent ${release.version} aktualisieren?`)) return;
    setUpdatingPointID(point.id); setError(""); setNotice("");
    try {
      await requestMeasurementPointDeviceUpdate(point.id);
      await reload();
      setNotice(`Update auf Agent ${release.version} wurde angefordert. Ein offline Gerät führt es beim nächsten Verbindungsaufbau aus.`);
    } catch (reason) { handleError(reason, "Das Update konnte nicht angefordert werden."); }
    finally { setUpdatingPointID(""); }
  }

  const visible = showAll ? points : points.slice(0, 5);
  return (
    <section className="management-page tenant-management">
      <div className="dashboard-heading management-heading">
        <div><h1>Messstellen</h1><p>Standorte und ihre aktuell zugewiesenen RPControl-Geräte für {identity.tenant}.</p></div>
      </div>

      {error && <p className="page-error" role="alert">{error}</p>}
      {notice && <p className="page-notice" role="status">{notice}</p>}

      <div className="device-table-wrap">
        <table className="management-table">
          <thead><tr><th>Messstelle</th><th>Zugewiesenes Gerät</th><th>Agent</th><th>Status</th><th>MQTT-IP</th><th>Speicher frei</th><th>SQL-Puffer</th><th>Technik</th></tr></thead>
          <tbody>
            {visible.map((point) => {
              const update = agentUpdateView(point, release);
              return <Fragment key={point.id}><tr>
                <td><strong>{point.location}</strong></td>
                <td>{point.device_id ? <><strong>{deviceLabel(point)}</strong><small>{point.cn}</small></> : <span className="certificate-open">Nicht zugewiesen</span>}</td>
                <td>{point.status?.agent_version || "–"}<small>{point.status?.last_seen ? `Zuletzt ${new Date(point.status.last_seen).toLocaleString(displayLocale)}` : "Noch kein Heartbeat"}</small>{update.label && <small>{update.label}</small>}</td>
                <td><span className={`state-badge ${point.status?.online ? "online" : point.device_id ? "offline" : "pending"}`}><i />{point.status?.online ? "Online" : point.device_id ? "Offline" : "Offen"}</span></td>
                <td className="device-address-cell">{point.status?.local_address ? <><code>{point.status.local_address}</code><small>{point.status.online ? "MQTT-Verbindung" : "Zuletzt gemeldet"}</small></> : <><strong>–</strong><small>{point.device_id ? "Noch nicht gemeldet" : "Kein Gerät"}</small></>}</td>
                <DeviceStorageCells storage={point.status?.storage} online={point.status?.online} />
                <td><div className="table-actions">
                  {point.device_id && <button className="text-action" type="button" aria-expanded={extensionPointID === point.id} aria-controls={`point-extensions-${point.id}`} onClick={() => setExtensionPointID(current => current === point.id ? "" : point.id)}>Extensions</button>}
                  {point.device_id && supportsRemoteUpdates(point) && !updateRequiresMaintenance(point, release) && release && isAgentReleaseNewer(point, release) && <button className="text-action" type="button" disabled={update.pending || updatingPointID === point.id} onClick={() => void updateAgent(point)}>{update.retryable ? "Update erneut" : "Aktualisieren"}</button>}
                </div></td>
              </tr>{extensionPointID === point.id && <tr id={`point-extensions-${point.id}`} className="tenant-extension-detail"><td colSpan={8}><ExtensionHealth id={point.id} /></td></tr>}</Fragment>
            ;})}
            {!loading && points.length === 0 && <tr><td colSpan={8} className="table-empty">Noch keine Messstellen angelegt.</td></tr>}
          </tbody>
        </table>
        {loading && <p className="subtle-loading table-loading">Messstellen werden geladen …</p>}
      </div>
      {points.length > 5 && <button className="list-disclosure device-list-disclosure" type="button" onClick={() => setShowAll((current) => !current)}>{showAll ? "Auf fünf Messstellen reduzieren" : `Alle ${points.length} Messstellen anzeigen`}</button>}
    </section>
  );
}
